/**
 * Piloter › Rapports (/piloter/rapports).
 * Sous-onglet « État mensuel » : entrées/sorties de l'année, groupées par cuvée/millésime
 * (domaine), ventilées par mois (mois courant → janvier), classées par couleur puis nom.
 * Totaux mensuels + annuel, export PDF.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';
import { piloterApi } from '../../services/api';
import { Button, Card, Spinner, PageTitle } from '../../components/ui/kit';
import { WINE_COLOR_DOTS } from '../../constants';

const MONTHS_FR = ['', 'Janv.', 'Févr.', 'Mars', 'Avril', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];

export default function Rapports() {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [downloading, setDownloading] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['monthly-report', year], queryFn: () => piloterApi.monthlyReport(year) });

  const download = async () => {
    setDownloading(true);
    try { await piloterApi.downloadMonthlyReport(year); }
    catch (e) { toast.error('Téléchargement impossible'); }
    finally { setDownloading(false); }
  };

  const months = data?.months || [];
  const dotColor = (c) => WINE_COLOR_DOTS[c]?.color || 'oklch(0.6 0.02 60)';

  // Insère un en-tête de couleur quand la couleur change.
  const rowsWithHeaders = [];
  let lastColor = null;
  (data?.rows || []).forEach((g) => {
    if (g.color !== lastColor) { rowsWithHeaders.push({ _header: true, color: g.color, label: g.color_label }); lastColor = g.color; }
    rowsWithHeaders.push(g);
  });

  return (
    <div className="p-4 md:p-6">
      <PageTitle kicker="Piloter" title="Rapports" />

      {/* Sous-onglets */}
      <div className="flex gap-2 mb-5">
        <button className="vd-btn vd-btn-primary h-8 text-[13px]" data-testid="report-subtab-mensuel">
          <FileBarChart className="w-4 h-4" /> État mensuel
        </button>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <label className="flex items-center gap-2 text-[13px]">
            <span className="text-n600">Année</span>
            <select className="vd-input h-9" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="report-year-select">
              <option value={nowYear}>Cette année {nowYear}</option>
              <option value={nowYear - 1}>L'an dernier {nowYear - 1}</option>
            </select>
          </label>
          <Button variant="primary" onClick={download} disabled={downloading} data-testid="report-download-pdf">
            <Download className="w-4 h-4" /> {downloading ? '…' : 'Télécharger le rapport PDF'}
          </Button>
        </div>

        {isLoading ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse" data-testid="monthly-report-table">
              <thead>
                <tr className="text-left kicker align-bottom">
                  <th className="py-2 px-2 sticky left-0 bg-paper">Domaine</th>
                  <th className="py-2 px-2">Cuvée</th>
                  <th className="py-2 px-2">Mill.</th>
                  {months.map((m) => <th key={m} className="py-2 px-2 text-center whitespace-nowrap">{MONTHS_FR[m]}</th>)}
                  <th className="py-2 px-2 text-center text-emerald-700">Tot. E</th>
                  <th className="py-2 px-2 text-center text-red-700">Tot. S</th>
                  <th className="py-2 px-2 text-center">Stock</th>
                </tr>
              </thead>
              <tbody>
                {rowsWithHeaders.length === 0 && (
                  <tr><td colSpan={months.length + 6} className="py-6 text-center text-n500">Aucun mouvement enregistré pour {year}.</td></tr>
                )}
                {rowsWithHeaders.map((g, i) => g._header ? (
                  <tr key={`h-${i}`} className="bg-surface/60" data-testid={`report-color-${g.color}`}>
                    <td colSpan={months.length + 6} className="py-1.5 px-2 font-title text-[14px] flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full border border-hair" style={{ background: dotColor(g.color) }} /> {g.label}
                    </td>
                  </tr>
                ) : (
                  <tr key={g.cuvee_id + '-' + g.year} className="hair-t hover:bg-surface/40" data-testid={`report-row-${g.cuvee_id}-${g.year}`}>
                    <td className="py-1.5 px-2 sticky left-0 bg-paper">{g.domain}</td>
                    <td className="py-1.5 px-2 font-medium">{g.name}</td>
                    <td className="py-1.5 px-2 text-n600">{g.year || '—'}</td>
                    {months.map((m) => {
                      const mm = g.by_month[m] || { in: 0, out: 0 };
                      return (
                        <td key={m} className="py-1.5 px-2 text-center whitespace-nowrap">
                          {(mm.in || mm.out) ? (
                            <span>
                              {mm.in ? <span className="text-emerald-700">+{mm.in}</span> : null}
                              {mm.in && mm.out ? <span className="text-n400"> </span> : null}
                              {mm.out ? <span className="text-red-700">−{mm.out}</span> : null}
                            </span>
                          ) : <span className="text-n300">·</span>}
                        </td>
                      );
                    })}
                    <td className="py-1.5 px-2 text-center font-medium text-emerald-700">{g.total_in}</td>
                    <td className="py-1.5 px-2 text-center font-medium text-red-700">{g.total_out}</td>
                    <td className="py-1.5 px-2 text-center font-medium">{g.stock}</td>
                  </tr>
                ))}
              </tbody>
              {data && (
                <tfoot>
                  <tr className="border-t-2 border-gold font-title text-[13px]" data-testid="monthly-report-totals">
                    <td className="py-2 px-2 sticky left-0 bg-paper" colSpan={3}>Total bouteilles</td>
                    {months.map((m) => {
                      const mt = data.monthly_totals[m] || { in: 0, out: 0 };
                      return (
                        <td key={m} className="py-2 px-2 text-center whitespace-nowrap">
                          <span className="text-emerald-700">+{mt.in}</span> <span className="text-red-700">−{mt.out}</span>
                        </td>
                      );
                    })}
                    <td className="py-2 px-2 text-center text-emerald-700">{data.total_in}</td>
                    <td className="py-2 px-2 text-center text-red-700">{data.total_out}</td>
                    <td className="py-2 px-2 text-center">—</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
